/**
 * CompanyBlankFormPage — iter-293 (Feb 2026)
 * ===========================================
 * A multi-page, free-text, print-only "فورم الشركة" (Company Blank Form).
 *
 * Flow:
 *   1) User picks the form language (Arabic or English).
 *   2) The editor opens — each A4 page shows a locked brand header +
 *      a free contentEditable middle + a locked brand footer.
 *   3) The script of the opposite language is stripped on input/paste.
 *   4) "صفحة جديدة" adds a new blank A4 page.
 *   5) "تحميل PDF" + "طباعة" open a new window using the unified brand
 *      style (same engine as receipts & quotations) — nothing is saved.
 *
 * Route: /manage/cloudgm-fz-llc/blank-form
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, Plus, Printer, Download, Globe, Trash2, ArrowLeft,
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Type, Heading1, Heading2, ImagePlus,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { printBlankForm, fetchBlankFormBrand } from '../../utils/printBlankForm';

/* ───────── Script helpers ──────────────────────────────────────── */
// Keep Arabic script + digits + punctuation + whitespace.
const AR_KEEP = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s\d.,:;!?()[\]{}\-–—_"'“”‘’«»،؛؟/@#%&*+=<>|\\\n\r\t]/;
// Keep Basic Latin + digits + punctuation.
const EN_KEEP = /[A-Za-z\s\d.,:;!?()[\]{}\-–—_"'“”‘’/@#%&*+=<>|\\\n\r\t]/;

const filterText = (text, lang) => {
  if (!text) return '';
  const re = lang === 'ar' ? AR_KEEP : EN_KEEP;
  return Array.from(text).filter((c) => re.test(c)).join('');
};

/* ───────── Rich-text formatting toolbar ─────────────────────────── */
// Runs document.execCommand against the editor that currently has
// selection focus. We stash the last-focused editor id on the window
// so toolbar clicks (which steal focus for a moment) still target the
// right page body.
const applyCmd = (cmd, value = null) => {
  const el = window._bfLastEditor;
  if (el && document.activeElement !== el) el.focus();
  try { document.execCommand(cmd, false, value); } catch { /* noop */ }
};
const applyFontSize = (px) => {
  const el = window._bfLastEditor;
  if (!el) return;
  el.focus();
  // execCommand('fontSize') only accepts 1-7; wrap selection in a span
  // with inline style instead so we can set exact pixel sizes.
  const sel = window.getSelection();
  if (!sel.rangeCount || sel.isCollapsed) {
    // No selection — apply to the whole editor
    el.style.fontSize = px;
    return;
  }
  const range = sel.getRangeAt(0);
  const span = document.createElement('span');
  span.style.fontSize = px;
  try {
    range.surroundContents(span);
  } catch {
    // surroundContents fails if selection spans multiple blocks — fallback
    document.execCommand('fontSize', false, 4);
    const fonts = el.querySelectorAll('font[size="4"]');
    fonts.forEach((f) => { f.removeAttribute('size'); f.style.fontSize = px; });
  }
};
const applyFontFamily = (family) => {
  applyCmd('fontName', family);
};

/* ───────── Image insertion (draggable floating image) ───────── */
// Reads a File -> dataURL and injects a draggable floating image span.
// We make the span contenteditable=false so the caret cannot land in it
// and the input-filter (which walks text nodes) never touches it.
const insertImageDataUrl = (dataUrl) => {
  const el = window._bfLastEditor;
  if (!el) return;
  el.focus();
  const id = `img-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  // 40/40 offset keeps the image inside the editor on first drop.
  const html = `
<span class="bf-drag-img" contenteditable="false" data-img-id="${id}"
      style="position:absolute; left:40px; top:40px; z-index:5;
             cursor:move; user-select:none; display:inline-block;
             padding:2px; border-radius:6px; border:1px dashed transparent;">
  <img src="${dataUrl}" draggable="false"
       style="max-width:260px; width:260px; height:auto; display:block; pointer-events:none; border-radius:4px;" />
</span>&#8203;`;
  // Insert at caret and let the browser place it in the current block.
  document.execCommand('insertHTML', false, html);
};

const openImagePicker = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.onchange = () => {
    const file = input.files && input.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => insertImageDataUrl(reader.result);
    reader.readAsDataURL(file);
  };
  input.click();
};

const RichToolbar = ({ lang, isAr, t }) => {
  const Btn = ({ onClick, title, children, testid }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick}
            title={title} data-testid={testid}
            className="h-8 w-8 rounded hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors">
      {children}
    </button>
  );
  const Divider = () => <div className="h-5 w-px bg-white/10 mx-1" />;

  const arFonts  = ['Cairo', 'Tajawal', 'Amiri', 'Noto Naskh Arabic'];
  const enFonts  = ['Inter', 'Georgia', 'Arial', 'Courier New'];
  const fontList = lang === 'ar' ? arFonts : enFonts;

  const sizes = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

  return (
    <div className="sticky top-16 z-30 bg-slate-950/85 backdrop-blur-xl rounded-xl border border-white/10 p-2 mb-4
                    flex flex-wrap items-center gap-1 justify-start" dir="ltr"
         data-testid="blank-form-rich-toolbar">
      {/* Font size */}
      <div className="flex items-center gap-1 px-1">
        <Type className="h-3.5 w-3.5 text-white/40" />
        <select onChange={(e) => applyFontSize(e.target.value)}
                defaultValue="14px"
                className="bg-black/40 border border-white/10 rounded text-white/80 text-[11px] px-1 py-0.5 cursor-pointer"
                data-testid="rtb-font-size">
          {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <Divider />
      {/* Font family */}
      <select onChange={(e) => applyFontFamily(e.target.value)}
              defaultValue={fontList[0]}
              className="bg-black/40 border border-white/10 rounded text-white/80 text-[11px] px-2 py-1 cursor-pointer min-w-[90px]"
              data-testid="rtb-font-family">
        {fontList.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
      </select>
      <Divider />
      {/* Bold / Italic / Underline */}
      <Btn onClick={() => applyCmd('bold')} title={t('غامق', 'Bold')} testid="rtb-bold">
        <Bold className="h-4 w-4" />
      </Btn>
      <Btn onClick={() => applyCmd('italic')} title={t('مائل', 'Italic')} testid="rtb-italic">
        <Italic className="h-4 w-4" />
      </Btn>
      <Btn onClick={() => applyCmd('underline')} title={t('تحته خط', 'Underline')} testid="rtb-underline">
        <Underline className="h-4 w-4" />
      </Btn>
      <Divider />
      {/* Headings */}
      <Btn onClick={() => applyCmd('formatBlock', 'H1')} title={t('عنوان كبير', 'Heading 1')} testid="rtb-h1">
        <Heading1 className="h-4 w-4" />
      </Btn>
      <Btn onClick={() => applyCmd('formatBlock', 'H2')} title={t('عنوان متوسط', 'Heading 2')} testid="rtb-h2">
        <Heading2 className="h-4 w-4" />
      </Btn>
      <Btn onClick={() => applyCmd('formatBlock', 'P')} title={t('فقرة عادية', 'Paragraph')} testid="rtb-p">
        <span className="text-[11px] font-semibold">¶</span>
      </Btn>
      <Divider />
      {/* Lists */}
      <Btn onClick={() => applyCmd('insertUnorderedList')} title={t('قائمة نقطية', 'Bulleted list')} testid="rtb-ul">
        <List className="h-4 w-4" />
      </Btn>
      <Btn onClick={() => applyCmd('insertOrderedList')} title={t('قائمة مرقمة', 'Numbered list')} testid="rtb-ol">
        <ListOrdered className="h-4 w-4" />
      </Btn>
      <Divider />
      {/* Alignment */}
      <Btn onClick={() => applyCmd('justifyLeft')}   title={t('محاذاة لليسار', 'Align left')}   testid="rtb-align-left">
        <AlignLeft className="h-4 w-4" />
      </Btn>
      <Btn onClick={() => applyCmd('justifyCenter')} title={t('توسيط', 'Align center')} testid="rtb-align-center">
        <AlignCenter className="h-4 w-4" />
      </Btn>
      <Btn onClick={() => applyCmd('justifyRight')}  title={t('محاذاة لليمين', 'Align right')} testid="rtb-align-right">
        <AlignRight className="h-4 w-4" />
      </Btn>
      <Divider />
      {/* Insert image */}
      <Btn onClick={openImagePicker} title={t('إدراج صورة', 'Insert image')} testid="rtb-image">
        <ImagePlus className="h-4 w-4 text-sky-300" />
      </Btn>
    </div>
  );
};

/* ───────── Page editor sub-component ───────────────────────────── */
const PageEditor = ({ pageIndex, lang, onChange, onDelete, canDelete, brand, isAr }) => {
  const ref = useRef(null);
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const font = lang === 'ar' ? "'Cairo', 'Segoe UI', sans-serif" : "'Inter', 'Segoe UI', sans-serif";

  // Accept pasted images (from clipboard) AND filter pasted text by script.
  const handlePaste = useCallback((e) => {
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;

    // ── 1) Image on the clipboard? Insert it as a draggable floating image.
    const items = cd.items ? Array.from(cd.items) : [];
    const imageItem = items.find((it) => it.kind === 'file' && it.type && it.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => insertImageDataUrl(reader.result);
      reader.readAsDataURL(file);
      return;
    }

    // ── 2) HTML on the clipboard (e.g. from Word / Google Docs)?
    //      Accept it but still strip opposite-script letters inside text nodes.
    const html = cd.getData('text/html');
    if (html && html.trim()) {
      e.preventDefault();
      // Parse the pasted HTML in a detached container, strip disallowed chars
      // from every text node, then insert the sanitised HTML.
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      // Remove any <script> and inline event handlers for safety.
      tmp.querySelectorAll('script, style, meta, link').forEach((n) => n.remove());
      tmp.querySelectorAll('*').forEach((n) => {
        [...n.attributes].forEach((a) => {
          if (/^on/i.test(a.name)) n.removeAttribute(a.name);
        });
      });
      // Filter text nodes.
      const walker = document.createTreeWalker(tmp, NodeFilter.SHOW_TEXT, null);
      let node = walker.nextNode();
      while (node) {
        node.nodeValue = filterText(node.nodeValue || '', lang);
        node = walker.nextNode();
      }
      document.execCommand('insertHTML', false, tmp.innerHTML);
      return;
    }

    // ── 3) Plain text — filter and insert.
    e.preventDefault();
    const text = cd.getData('text/plain') || '';
    const clean = filterText(text, lang);
    document.execCommand('insertText', false, clean);
  }, [lang]);

  // Strip opposite-script characters on direct input.
  const handleInput = useCallback(() => {
    if (!ref.current) return;
    const el = ref.current;
    const html = el.innerHTML;
    // Lightweight filter: iterate over text nodes only, keep structural tags.
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    const nodes = [];
    let node = walker.nextNode();
    while (node) { nodes.push(node); node = walker.nextNode(); }
    let changed = false;
    nodes.forEach((n) => {
      // Skip text inside non-editable image wrappers.
      if (n.parentNode && n.parentNode.closest && n.parentNode.closest('.bf-drag-img')) return;
      const v = n.nodeValue || '';
      const cleaned = filterText(v, lang);
      if (cleaned !== v) { n.nodeValue = cleaned; changed = true; }
    });
    if (changed) {
      // Restore caret to end if we mutated nodes.
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    const finalHtml = ref.current.innerHTML;
    onChange(pageIndex, finalHtml);
    // Avoid unused variable lint on html.
    void html;
  }, [lang, onChange, pageIndex]);

  /* Drag-and-drop for floating images.
     Mounted once per editor; uses pointer events so it works on both mouse
     and touch.  While dragging we set `position:absolute` + updated left/top
     on the wrapper span.  After the drag ends we persist the new HTML. */
  useEffect(() => {
    const editor = ref.current;
    if (!editor) return undefined;
    let dragging = null; // { el, startX, startY, origLeft, origTop }

    const onPointerDown = (ev) => {
      const target = ev.target.closest && ev.target.closest('.bf-drag-img');
      if (!target || !editor.contains(target)) return;
      // Avoid interfering with text selection outside images.
      ev.preventDefault();
      target.setPointerCapture?.(ev.pointerId);
      target.style.borderColor = '#38bdf8'; // highlight while dragging
      target.style.zIndex = '20';
      const rect = target.getBoundingClientRect();
      const editorRect = editor.getBoundingClientRect();
      dragging = {
        el: target,
        pointerId: ev.pointerId,
        offsetX: ev.clientX - rect.left,
        offsetY: ev.clientY - rect.top,
        editorRect,
      };
    };
    const onPointerMove = (ev) => {
      if (!dragging) return;
      const { el, offsetX, offsetY, editorRect } = dragging;
      // Compute new left/top relative to the editor.
      let newLeft = ev.clientX - editorRect.left - offsetX;
      let newTop  = ev.clientY - editorRect.top  - offsetY;
      // Keep inside the editor bounds (give 6px padding).
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      newLeft = Math.max(0, Math.min(newLeft, editorRect.width  - w));
      newTop  = Math.max(0, Math.min(newTop,  editorRect.height - h));
      el.style.left = `${newLeft}px`;
      el.style.top  = `${newTop}px`;
    };
    const onPointerUp = (ev) => {
      if (!dragging) return;
      const { el, pointerId } = dragging;
      try { el.releasePointerCapture?.(pointerId); } catch { /* noop */ }
      el.style.borderColor = 'transparent';
      el.style.zIndex = '5';
      dragging = null;
      // Persist the new HTML (position lives inline on the span).
      onChange(pageIndex, editor.innerHTML);
      // Avoid unused variable lint.
      void ev;
    };
    // Right-click image → delete it.
    const onContextMenu = (ev) => {
      const target = ev.target.closest && ev.target.closest('.bf-drag-img');
      if (!target || !editor.contains(target)) return;
      ev.preventDefault();
      const confirmed = window.confirm(lang === 'ar' ? 'حذف هذه الصورة؟' : 'Delete this image?');
      if (confirmed) {
        target.remove();
        onChange(pageIndex, editor.innerHTML);
      }
    };
    editor.addEventListener('pointerdown',  onPointerDown);
    editor.addEventListener('pointermove',  onPointerMove);
    editor.addEventListener('pointerup',    onPointerUp);
    editor.addEventListener('pointercancel', onPointerUp);
    editor.addEventListener('contextmenu',  onContextMenu);
    return () => {
      editor.removeEventListener('pointerdown',  onPointerDown);
      editor.removeEventListener('pointermove',  onPointerMove);
      editor.removeEventListener('pointerup',    onPointerUp);
      editor.removeEventListener('pointercancel', onPointerUp);
      editor.removeEventListener('contextmenu',  onContextMenu);
    };
  }, [lang, onChange, pageIndex]);

  const today = new Date().toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
  const NAME = lang === 'ar' ? brand.brand_name_ar : brand.brand_name_en;
  const LOGO = brand.logo_url || '/pdf-logo-cloudgm.png';
  const SITES = (brand.footer_sites || []).join(' · ');
  const PHONE = brand.footer_phone || '';
  const EMAILS = (brand.footer_emails || []).join(' · ');
  const TAG = lang === 'ar' ? brand.footer_tagline_ar : brand.footer_tagline_en;

  const FROM = brand.accent_from || '#3B9EDC';
  const TO   = brand.accent_to   || '#F5A55A';
  const grad = `linear-gradient(90deg, ${FROM} 0%, ${TO} 100%)`;

  return (
    <div
      className="relative bg-white rounded-lg shadow-2xl mx-auto"
      style={{ width: '820px', maxWidth: '95vw', padding: '32px', color: '#0f172a' }}
      dir={dir}
      data-testid={`blank-form-page-${pageIndex}`}
    >
      {/* Page badge + delete */}
      <div className={`absolute -top-3 ${lang === 'ar' ? '-left-3' : '-right-3'} flex items-center gap-2`}>
        <div
          className="px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg"
          style={{ background: grad }}
          dir="ltr"
        >
          {isAr ? `صفحة ${pageIndex + 1}` : `Page ${pageIndex + 1}`}
        </div>
        {canDelete && (
          <button
            onClick={() => onDelete(pageIndex)}
            className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors"
            title={isAr ? 'حذف الصفحة' : 'Delete page'}
            data-testid={`blank-form-delete-page-${pageIndex}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Header */}
      <div style={{ height: 6, background: grad, borderRadius: 2, marginBottom: 20 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 18 }}>
        <img
          src={LOGO}
          alt=""
          style={{ height: 56, width: 'auto' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a', fontFamily: font }}>
            {NAME}
          </h1>
        </div>
        <div style={{ textAlign: lang === 'ar' ? 'left' : 'right', fontSize: 10, color: '#64748b' }}>
          {today}
        </div>
      </div>

      {/* Editable body */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        dir={dir}
        onFocus={() => { window._bfLastEditor = ref.current; }}
        onInput={handleInput}
        onPaste={handlePaste}
        style={{
          minHeight: '550px',
          fontFamily: font,
          fontSize: 13,
          lineHeight: 1.9,
          color: '#0f172a',
          outline: 'none',
          padding: '6px',
          direction: dir,
          textAlign: lang === 'ar' ? 'right' : 'left',
          borderRadius: 4,
          position: 'relative',   // anchor for absolute-positioned draggable images
        }}
        data-testid={`blank-form-editor-${pageIndex}`}
        data-placeholder={isAr ? 'ابدأ الكتابة هنا…' : 'Start typing here…'}
      />

      {/* Footer */}
      <div style={{ marginTop: 22, paddingTop: 12, borderTop: '1px solid #e2e8f0', textAlign: 'center', color: '#64748b', fontSize: 9 }}>
        <div style={{ height: 2, background: grad, marginBottom: 10, borderRadius: 999 }} />
        {TAG && <div style={{ fontWeight: 700, color: FROM, marginBottom: 4, fontSize: 11 }}>{TAG}</div>}
        <div style={{ margin: '2px 0', direction: 'ltr' }}>🌐 {SITES}</div>
        <div style={{ margin: '2px 0', direction: 'ltr' }}>📞 {PHONE}</div>
        <div style={{ margin: '2px 0', direction: 'ltr' }}>✉ {EMAILS}</div>
        <div style={{ margin: '6px 0 0', direction: 'ltr' }}>{NAME}</div>
      </div>
    </div>
  );
};

/* ───────── Language picker screen ──────────────────────────────── */
const LanguagePicker = ({ onPick, isAr }) => {
  const options = [
    { code: 'ar', labelAr: 'العربية',  labelEn: 'Arabic',  flag: '🇸🇦', dir: 'rtl', color: '#3B9EDC' },
    { code: 'en', labelAr: 'الإنجليزية', labelEn: 'English', flag: '🇬🇧', dir: 'ltr', color: '#F5A55A' },
  ];
  return (
    <div className="max-w-3xl mx-auto py-16 px-4" data-testid="blank-form-language-picker">
      <div className="text-center mb-10">
        <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
             style={{ background: 'linear-gradient(135deg, #3B9EDC22 0%, #F5A55A22 100%)', border: '1px solid #3B9EDC44' }}>
          <Globe className="h-8 w-8 text-sky-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          {isAr ? 'اختر لغة الفورم' : 'Choose form language'}
        </h2>
        <p className="text-sm text-white/60">
          {isAr
            ? 'بعد الاختيار سيُفتح المحرر وسيقبل فقط حروف اللغة المختارة ويضبط اتجاه الكتابة تلقائياً.'
            : 'After choosing, the editor will accept only the selected language characters and will auto-set the writing direction.'}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {options.map((o, i) => (
          <motion.button
            key={o.code}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onPick(o.code)}
            className="bg-white/[0.03] backdrop-blur-xl rounded-2xl p-8 border transition-all text-center"
            style={{ borderColor: `${o.color}40` }}
            data-testid={`blank-form-pick-lang-${o.code}`}
          >
            <div className="text-6xl mb-3">{o.flag}</div>
            <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: o.code === 'ar' ? 'Cairo, sans-serif' : 'Inter, sans-serif' }}>
              {isAr ? o.labelAr : o.labelEn}
            </h3>
            <p className="text-xs text-white/50 uppercase tracking-wider mt-2" dir="ltr">
              dir = {o.dir}
            </p>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

/* ───────── Main page ───────────────────────────────────────────── */
export default function CompanyBlankFormPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [formLang, setFormLang] = useState(null);   // 'ar' | 'en' | null
  const [pages, setPages] = useState(['']);          // array of HTML per page
  const [brand, setBrand] = useState({
    logo_url: '/pdf-logo-cloudgm.png',
    accent_from: '#3B9EDC', accent_to: '#F5A55A',
    brand_name_ar: 'CLOUDGM FZ LLC', brand_name_en: 'CLOUDGM FZ LLC',
    footer_sites: ['cloudgm.com', 'cloudgm.business'],
    footer_phone: '+971 50 151 1917',
    footer_emails: ['info@cloudgm.com', 'info@cloudgm.business'],
    footer_tagline_ar: 'شكراً لتعاملكم معنا',
    footer_tagline_en: 'Thank you for your business',
  });

  useEffect(() => {
    let alive = true;
    fetchBlankFormBrand().then((b) => { if (alive) setBrand(b); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const updatePage = useCallback((idx, html) => {
    setPages((prev) => {
      if (prev[idx] === html) return prev;
      const next = [...prev];
      next[idx] = html;
      return next;
    });
  }, []);

  const addPage = () => setPages((prev) => [...prev, '']);
  const deletePage = (idx) => {
    if (pages.length <= 1) return;
    const ok = window.confirm(t('هل تريد حذف هذه الصفحة؟', 'Delete this page?'));
    if (!ok) return;
    setPages((prev) => prev.filter((_, i) => i !== idx));
  };

  const doPrint = () => {
    printBlankForm(pages, { language: formLang, download: false });
  };
  const doDownload = () => {
    // Opens the print window and immediately triggers browser's Save-as-PDF.
    printBlankForm(pages, { language: formLang, download: true });
  };

  const changeLang = () => {
    const anyContent = pages.some((p) => (p || '').replace(/<[^>]*>/g, '').trim().length > 0);
    if (anyContent) {
      const ok = window.confirm(t(
        'تغيير اللغة سيمسح المحتوى الحالي. متابعة؟',
        'Changing language will erase the current content. Continue?',
      ));
      if (!ok) return;
    }
    setPages(['']);
    setFormLang(null);
  };

  return (
    <DashboardLayout
      title="فورم الشركة"
      titleEn="Company Blank Form"
      subtitle="فورم فارغ متعدد الصفحات بهوية CLOUDGM — للطباعة أو تحميل PDF مباشرة"
      subtitleEn="Blank multi-page document in CloudGM identity — print or download as PDF instantly"
      screenCode="FZLC-006"
      backPath="/manage/cloudgm-fz-llc"
      onBack={() => navigate('/manage/cloudgm-fz-llc')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'إدارة شركة CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC Management', path: '/manage/cloudgm-fz-llc' },
        { labelAr: 'فورم الشركة', labelEn: 'Company Blank Form' },
      ]}
    >
      {!formLang && <LanguagePicker onPick={setFormLang} isAr={isAr} />}

      {formLang && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="blank-form-editor-root">
          {/* Rich-text formatting toolbar (iter-297) */}
          <RichToolbar lang={formLang} isAr={isAr} t={t} />

          {/* Toolbar */}
          <div className="sticky top-16 z-20 bg-slate-950/80 backdrop-blur-xl rounded-xl border border-white/10 p-3 mb-6 flex flex-wrap items-center gap-2 justify-between" style={{ top: 'calc(4rem + 56px)' }}>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                <Globe className="h-4 w-4 text-sky-400" />
                <span className="text-sm text-white font-semibold" dir="ltr">
                  {formLang === 'ar' ? 'AR · RTL' : 'EN · LTR'}
                </span>
                <button
                  onClick={changeLang}
                  className="ms-2 text-[11px] text-sky-300 hover:text-sky-200 underline"
                  data-testid="blank-form-change-lang"
                >
                  {t('تغيير', 'Change')}
                </button>
              </div>
              <div className="text-xs text-white/50 hidden sm:block">
                {t(`${pages.length} صفحة`, `${pages.length} page${pages.length > 1 ? 's' : ''}`)}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={addPage}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold transition-colors"
                data-testid="blank-form-add-page"
              >
                <Plus className="h-4 w-4" />
                {t('صفحة جديدة', 'New page')}
              </button>
              <button
                onClick={doPrint}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold transition-colors"
                data-testid="blank-form-print"
              >
                <Printer className="h-4 w-4" />
                {t('طباعة', 'Print')}
              </button>
              <button
                onClick={doDownload}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-bold transition-opacity hover:opacity-90"
                style={{ background: `linear-gradient(90deg, ${brand.accent_from} 0%, ${brand.accent_to} 100%)` }}
                data-testid="blank-form-download"
              >
                <Download className="h-4 w-4" />
                {t('تحميل PDF', 'Download PDF')}
              </button>
            </div>
          </div>

          {/* Pages stack */}
          <div className="space-y-10 py-4">
            {pages.map((_, idx) => (
              <PageEditor
                key={idx}
                pageIndex={idx}
                lang={formLang}
                brand={brand}
                isAr={isAr}
                onChange={updatePage}
                onDelete={deletePage}
                canDelete={pages.length > 1}
              />
            ))}
          </div>

          {/* Bottom quick add */}
          <div className="flex justify-center mt-6">
            <button
              onClick={addPage}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-dashed border-white/20 text-white/80 text-sm font-semibold transition-colors"
              data-testid="blank-form-add-page-bottom"
            >
              <Plus className="h-4 w-4" />
              {t('إضافة صفحة أخرى', 'Add another page')}
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-white/40">
            <FileText className="h-3.5 w-3.5" />
            {t(
              'المحتوى موجود فقط في المتصفح — لا يتم حفظه في أي قاعدة بيانات.',
              'Content lives only in your browser — nothing is saved to any database.',
            )}
          </div>

          <div className="mt-4 flex items-center justify-center">
            <button
              onClick={() => navigate('/manage/cloudgm-fz-llc')}
              className="flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors"
              data-testid="blank-form-back"
            >
              <ArrowLeft className={`h-3.5 w-3.5 ${isAr ? 'rotate-180' : ''}`} />
              {t('رجوع لإدارة الشركة', 'Back to Company Management')}
            </button>
          </div>
        </section>
      )}

      {/* Global placeholder for contentEditable */}
      <style>{`
        [contenteditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #94a3b8;
          font-style: italic;
          pointer-events: none;
        }
      `}</style>
    </DashboardLayout>
  );
}
